import { rupees } from "../rates/rateModel";

/**
 * The one Import screen: what a checked file holds, and what was added. The server reads every tab of
 * the file; a studio gives what it has (often people first, rates weeks later) and adds the rest later.
 */
export type Line = { text: string; vars?: Record<string, string | number> };

export type Part = {
  key: "people" | "looms" | "rates" | "onLooms" | "advances" | "dailyWork";
  title: string;
  line: Line;
  from?: string;
  notes: Line[];
  warn: boolean;
};

const ORDER: Part["key"][] = ["people", "looms", "rates", "onLooms", "advances", "dailyWork"];

const TITLES: Record<Part["key"], string> = {
  people: "People",
  looms: "Looms",
  rates: "Rates",
  onLooms: "Sarees on looms now",
  advances: "Advances owed",
  dailyWork: "Old daily work",
};

/** Issue codes from the people check, said plainly. */
const ISSUE: Record<string, string> = {
  NO_CATEGORY: "no work given",
  UNKNOWN_WORK: "work not known",
  NAME_IN_HKP: "same name already in HKP",
  DUPLICATE_NAME_IN_FILE: "in the list twice",
  DUPLICATE_CODE_IN_FILE: "code in the list twice",
  CODE_IN_HKP: "code already in HKP",
  BLANK_NAME: "no name",
  NO_PREFIX: "code has no letters",
  UNKNOWN_PREFIX: "code letters not known",
};

const n = (v: unknown): number => (typeof v === "number" ? v : Number(v) || 0);

const shortDate = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

/** What the checked file would add, part by part, in the order it is added. */
export const checkedParts = (c: any): Part[] => {
  const out: Part[] = [];
  for (const key of ORDER) {
    const v = c?.[key];
    if (!v) continue;
    const p: Part = { key, title: TITLES[key], line: { text: "" }, from: v.from, notes: [], warn: false };
    if (key === "people") {
      p.line = { text: "{{n}} to add", vars: { n: n(v.ready) } };
      if (n(v.toCheck)) {
        p.warn = true;
        p.notes.push({ text: "{{n}} to check after adding (they wait in review)", vars: { n: n(v.toCheck) } });
        for (const r of v.rowsToCheck || []) {
          p.notes.push({ text: "Row {{row}} · {{who}}: {{why}}", vars: { row: r.row, who: r.name || r.code || "—", why: (r.issues || []).map((i: string) => ISSUE[i] || i).join(", ") } });
        }
      }
      if (n(v.skipped)) p.notes.push({ text: "{{n}} rows left out (not people, or already in HKP)", vars: { n: n(v.skipped) } });
    } else if (key === "looms") {
      p.line = n(v.alreadyIn) ? { text: "{{n}} new · {{a}} already in HKP", vars: { n: n(v.new), a: n(v.alreadyIn) } } : { text: "{{n}} new", vars: { n: n(v.new) } };
    } else if (key === "rates") {
      const designs: any[] = v.designs || [];
      const bad = designs.filter((d) => d.action === "ERROR" || d.action === "HAS_DRAFT");
      p.line = n(v.unchanged) ? { text: "{{n}} designs ready · {{u}} unchanged", vars: { n: n(v.ready), u: n(v.unchanged) } } : { text: "{{n}} designs ready", vars: { n: n(v.ready) } };
      p.warn = bad.length > 0;
      for (const d of bad.slice(0, 20)) p.notes.push({ text: "{{design}}: {{why}}", vars: { design: d.designNumber, why: (d.errors || [])[0] || "" } });
    } else if (key === "onLooms") {
      p.line = n(v.alreadyRunning)
        ? { text: "{{n}} sarees to start · {{a}} already on looms", vars: { n: n(v.rows), a: n(v.alreadyRunning) } }
        : { text: "{{n}} sarees to start", vars: { n: n(v.rows) } };
      for (const s of v.stopped || []) p.notes.push({ text: s });
    } else if (key === "advances") {
      p.line = { text: "{{n}} people · {{total}}", vars: { n: n(v.people), total: rupees(v.total ?? 0) } };
      p.warn = (v.problems || []).length > 0;
      for (const s of v.problems || []) p.notes.push({ text: s });
    } else if (key === "dailyWork") {
      p.line = { text: "{{n}} rows · {{first}} – {{last}}", vars: { n: n(v.rows), first: shortDate(v.firstDate), last: shortDate(v.lastDate) } };
      p.notes.push({ text: "Kept as history. It is never paid again." });
    }
    out.push(p);
  }
  return out;
};

/** What was added; a part that failed says why, the others still went in. */
export const addedParts = (r: any): Part[] => {
  const out: Part[] = [];
  for (const key of ORDER) {
    const v = r?.[key];
    if (!v) continue;
    const p: Part = { key, title: TITLES[key], line: { text: "" }, notes: [], warn: false };
    if (v.error) {
      p.line = { text: "Not added: {{why}}", vars: { why: v.error } };
      p.warn = true;
      out.push(p);
      continue;
    }
    if (key === "people") {
      p.line = { text: "{{n}} added", vars: { n: n(v.added) } };
      if (n(v.toCheck)) {
        p.warn = true;
        p.notes.push({ text: "{{n}} to check in review", vars: { n: n(v.toCheck) } });
      }
    } else if (key === "looms") {
      p.line = { text: "{{n}} added", vars: { n: n(v.added) } };
    } else if (key === "rates") {
      p.line = v.approved !== undefined ? { text: "{{n}} designs saved · {{a}} approved", vars: { n: n(v.saved), a: n(v.approved) } } : { text: "{{n}} designs saved as drafts", vars: { n: n(v.saved) } };
      if (n(v.unchanged)) p.notes.push({ text: "{{n}} designs already had these rates", vars: { n: n(v.unchanged) } });
      for (const d of (v.designs || []).filter((d: any) => (d.errors || []).length)) p.notes.push({ text: "{{design}}: {{why}}", vars: { design: d.designNumber, why: d.errors[0] } });
      p.warn = (v.designs || []).some((d: any) => (d.errors || []).length);
    } else if (key === "onLooms") {
      p.line = { text: "{{n}} sarees started", vars: { n: n(v.started) } };
      if (n(v.alreadyRunning)) p.notes.push({ text: "{{n}} looms already had their saree", vars: { n: n(v.alreadyRunning) } });
      for (const s of v.skipped || []) p.notes.push({ text: s });
      p.warn = (v.skipped || []).length > 0;
    } else if (key === "advances") {
      p.line = { text: "{{n}} recorded", vars: { n: n(v.recorded) } };
      if (n(v.alreadyInHkp)) p.notes.push({ text: "{{n}} already had their advances in HKP (change them in Pay)", vars: { n: n(v.alreadyInHkp) } });
      for (const s of v.skipped || []) p.notes.push({ text: s });
      p.warn = p.notes.length > 0;
    } else if (key === "dailyWork") {
      if (n(v.alreadyKept)) {
        p.line = { text: "Already kept before", vars: {} };
        out.push(p);
        continue;
      }
      const repeats = n(v.issueCounts?.ALREADY_IMPORTED) + n(v.issueCounts?.DUPLICATE_IN_FILE);
      if (v.status && v.status !== "KEPT") {
        // Only the owner keeps history; a manager's file waits for them.
        p.line = { text: "{{n}} rows checked · the owner keeps them in Old sheet", vars: { n: n(v.rows) - repeats } };
        p.warn = true;
        out.push(p);
        continue;
      }
      p.line = { text: "{{n}} rows kept as history", vars: { n: n(v.rows) - repeats } };
      if (repeats) p.notes.push({ text: "{{n}} rows were repeats and left out", vars: { n: repeats } });
      if (n(v.problems) - repeats > 0) p.notes.push({ text: "{{n}} rows have something to look at", vars: { n: n(v.problems) - repeats } });
    }
    out.push(p);
  }
  return out;
};

/** What the file didn't have, as things to add later; never an error. */
export const MISSING: Record<string, Line> = {
  PEOPLE: { text: "People: add them here later, or one by one as they come" },
  LOOMS: { text: "Looms: add a range like 1-12 on Home" },
  RATES: { text: "Rates: work is saved and paid once rates are filled" },
  ON_LOOMS: { text: "Sarees on looms: start them from Daily work" },
  ADVANCES: { text: "Advances owed: give or note them in Pay" },
};
